import { designTokens } from "@itandi/itandi-bb-ui";
import { Badge } from "@itandi/itandi-bb-ui/Badge";
import { Breadcrumbs } from "@itandi/itandi-bb-ui/Breadcrumbs";
import { Button } from "@itandi/itandi-bb-ui/Button";
import { Flex } from "@itandi/itandi-bb-ui/Flex";
import { Heading } from "@itandi/itandi-bb-ui/Heading";
import { Label } from "@itandi/itandi-bb-ui/Label";
import { useNotification } from "@itandi/itandi-bb-ui/NotificationBarStore";
import { PresentationChip } from "@itandi/itandi-bb-ui/PresentationChip";
import { PrimaryTab } from "@itandi/itandi-bb-ui/PrimaryTab";
import { PrimaryTabs } from "@itandi/itandi-bb-ui/PrimaryTabs";
import { TextLink } from "@itandi/itandi-bb-ui/TextLink";
import { YokuaruHeader } from "@itandi/itandi-bb-ui/YokuaruHeader";
import Link from "next/link";
import { useRouter } from "next/router";
import type { ComponentProps, ReactNode } from "react";
import { useCallback, useMemo } from "react";
import useSWRMutation from "swr/mutation";
import { SimpleHeaderClientSP } from "~/components/common/layouts/deals/simple_header/SimpleHeaderClientSP";
import { DealTypeLabel } from "~/components/common/parts/label/DealTypeLabel";
import { useOwnerExplanationSuggestionExecutable } from "~/hooks/deals/useOwnerExplanationSuggestionExecutable";
import { MEDIA_QUERY, useMediaQuery } from "~/hooks/useMediaQuery";
import { useUsingApplications } from "~/hooks/useUsingApplications";
import type { Deal } from "~/models/Deal";
import { formatPropertyName } from "~/models/Deal";
import { getOriginDealVendor } from "~/models/DealVendorCompany";
import type { Message } from "~/models/Message";
import { getUnreadCount } from "~/models/Message";
import { PATH as DEAL_PATH } from "~/pages/deals/[id]";
import { PATH as RELATED_DEAL_NEW_PATH } from "~/pages/deals/[id]/related_deals/new";
import { PATH as PROPERTY_DEAL_PATH } from "~/pages/property_deals/[id]";
import { getSkPartnerDeal } from "~/utils/api/Deal";
import {
	editRentParkingSpaceUrl,
	editRentRoomBuildingUrl,
	editRentRoomUrl
} from "~/utils/BukkenKanriPath";
import { generatePath } from "~/utils/generatePath";
import { leasingUrl } from "~/utils/NyukyoshaKanriUrlUtil";
import { getSkFullPath, PATH } from "~/utils/SeisanKanriPath";

type Props = {
	deal: Deal;
	enableChat: boolean;
	isChatOpen: boolean;
	isV2DealDetail: boolean;
	items: ComponentProps<
		typeof Breadcrumbs | typeof SimpleHeaderClientSP
	>["items"];
	messages: Message[];
	onToggleChat?: () => void;
	useSimpleSpHeader: boolean;
};

type TabItem = {
	href?: string;
	isActive: boolean;
	label: string;
	onClick?: () => Promise<void>;
	rightArea?: ReactNode;
};

export function DealLayoutHeaderClient({
	deal,
	enableChat,
	isChatOpen,
	isV2DealDetail,
	items,
	messages,
	onToggleChat,
	useSimpleSpHeader
}: Props) {
	const { notify } = useNotification();
	const router = useRouter();
	const { match: isSp } = useMediaQuery(MEDIA_QUERY.sp);
	const { match: isSingleColumn } = useMediaQuery(MEDIA_QUERY.singleColumn);
	const { useSK } = useUsingApplications();
	const handleAddRelatedDealClick = (): void => {
		const href = RELATED_DEAL_NEW_PATH(deal.id);
		window.open(href, "_blank");
	};

	const { data: ownerExplanationSuggestionExecutable } =
		useOwnerExplanationSuggestionExecutable(deal.id);
	const showAiOwnerExplanationSuggestionChip =
		ownerExplanationSuggestionExecutable?.executable ?? false;

	const { trigger: triggerGetSkPartnerDeal } = useSWRMutation(
		generatePath("/deals/:id/sk_partner_deal", { id: deal.id }),
		() => getSkPartnerDeal(deal.id),
		{
			onError: () => {
				notify({ content: "精算情報の取得に失敗しました。", variant: "error" });
				void router.push(
					generatePath("/deals/:deal_id/partner_deals", { deal_id: deal.id })
				);
			},
			onSuccess: (response) => {
				if (response.linked && response.id !== null) {
					window.location.href = getSkFullPath(PATH.partnerDeal(response.id));
				} else {
					void router.push(
						generatePath("/deals/:deal_id/partner_deals", { deal_id: deal.id })
					);
				}
			}
		}
	);

	const handleSeisanTabClick = useCallback(async (): Promise<void> => {
		await triggerGetSkPartnerDeal();
	}, [triggerGetSkPartnerDeal]);

	const unreadMessageCount = useMemo((): number => {
		return getUnreadCount(messages, "maintenance_company");
	}, [messages]);

	const productLinkItems = useMemo((): { href: string; label: string }[] => {
		const items: { href: string; label: string }[] = [];
		if (deal.propertyType === "room" && deal.propertyId !== null) {
			items.push({ href: editRentRoomUrl(deal.propertyId), label: "物件詳細" });
		}
		if (deal.propertyType === "building" && deal.propertyId !== null) {
			items.push({
				href: editRentRoomBuildingUrl(deal.propertyId),
				label: "物件詳細"
			});
		}
		if (deal.propertyType === "parking_space" && deal.propertyId !== null) {
			items.push({
				href: editRentParkingSpaceUrl(deal.propertyId),
				label: "物件詳細"
			});
		}
		if (deal.leasingId !== null) {
			items.push({ href: leasingUrl(deal.leasingId), label: "賃貸借契約" });
		}
		return items;
	}, [deal]);

	const tabs = useMemo((): TabItem[] => {
		const { pathname } = router;

		const partnerDealsActive = pathname.startsWith("/deals/[id]/partner_deals");
		const pastDealsActive = pathname.startsWith("/deals/[id]/past_deals");
		const dealDetailActive =
			pathname.startsWith("/deals/[id]") &&
			!partnerDealsActive &&
			!pastDealsActive;

		const items: TabItem[] = [];
		items.push({
			href: DEAL_PATH(deal.id),
			isActive: dealDetailActive,
			label: "案件詳細"
		});
		if (useSK) {
			items.push({
				isActive: partnerDealsActive,
				label: "精算情報",
				onClick: handleSeisanTabClick
			});
		}
		items.push({
			href: `${PROPERTY_DEAL_PATH(deal.propertyDealId)}?dealId=${deal.id}`,
			isActive: router.pathname === "/property_deals/[id]",
			label: "関連する案件"
		});
		items.push({
			href: generatePath("/deals/:deal_id/past_deals", { deal_id: deal.id }),
			isActive: pastDealsActive,
			label: "過去案件履歴",
			rightArea: showAiOwnerExplanationSuggestionChip ? (
				<PresentationChip variant="purple">AI利用可</PresentationChip>
			) : undefined
		});
		return items;
	}, [
		deal,
		handleSeisanTabClick,
		router,
		showAiOwnerExplanationSuggestionChip,
		useSK
	]);

	return (
		<>
			{useSimpleSpHeader && isSp ? (
				<SimpleHeaderClientSP deal={deal} items={items} />
			) : (
				<YokuaruHeader
					breadcrumbs={<Breadcrumbs items={items} />}
					heading={
						<Flex
							alignItems="center"
							gap={designTokens["spacing/s"]}
							wrap="wrap"
						>
							<DealTypeLabel
								dealSubtype={deal.dealSubtype}
								dealType={deal.dealType}
							/>
							<Heading as="h1">{formatPropertyName(deal)}</Heading>
							<Flex direction="column">
								<Label color="subtle" size="s">
									{deal.address}
								</Label>
								<Label color="subtle" size="s">
									{`依頼先: ${getOriginDealVendor(deal)?.companyVendor?.name ?? ""}`}
								</Label>
							</Flex>
							{productLinkItems.length > 0 ? (
								<Flex
									alignItems="center"
									gap={designTokens["spacing/xs"]}
									wrap="wrap"
								>
									{productLinkItems.map((link) => (
										<TextLink
											color="link"
											href={link.href}
											key={link.href}
											size="s"
											target="_blank"
										>
											{link.label}
										</TextLink>
									))}
								</Flex>
							) : null}
						</Flex>
					}
					primaryTabs={
						isV2DealDetail && tabs.some((tab) => tab.isActive) ? (
							<PrimaryTabs value={tabs.find((tab) => tab.isActive)!.label}>
								{tabs.map((tab) =>
									tab.onClick !== undefined ? (
										<PrimaryTab
											key={tab.label}
											onClick={tab.onClick}
											rightArea={tab.rightArea}
											value={tab.label}
										>
											{tab.label}
										</PrimaryTab>
									) : (
										<PrimaryTab
											as={Link}
											href={tab.href}
											key={tab.label}
											rightArea={tab.rightArea}
											value={tab.label}
										>
											{tab.label}
										</PrimaryTab>
									)
								)}
							</PrimaryTabs>
						) : undefined
					}
					rightArea={
						<Flex
							alignItems="flex-end"
							direction={isSp ? "column" : "row"}
							gap={designTokens["spacing/s"]}
							justifyContent="end"
						>
							{enableChat && !isSingleColumn && onToggleChat ? (
								<Button
									endIcon={
										unreadMessageCount > 0 ? (
											<Badge count={unreadMessageCount} variant="unread" />
										) : undefined
									}
									height="small"
									onClick={onToggleChat}
									variant="secondary"
								>
									{isChatOpen
										? "チャットを閉じる"
										: `施工会社とチャット${deal.messageSummaryUnread?.displayUnread && unreadMessageCount === 0 ? `（未読あり）` : ""}`}
								</Button>
							) : null}
							{!isV2DealDetail && (
								<>
									<TextLink
										as={Link}
										href={PROPERTY_DEAL_PATH(deal.propertyDealId)}
									>
										{deal.relatedDeals.length === 1
											? "関連する案件一覧"
											: `他${deal.relatedDeals.length - 1}件の関連する案件`}
									</TextLink>
									<Button
										height="small"
										onClick={handleAddRelatedDealClick}
										variant="secondary"
									>
										関連する案件追加
									</Button>
								</>
							)}
						</Flex>
					}
					sticky
				/>
			)}
		</>
	);
}
